import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { contasApi, pluggyVinculosApi } from '../../api/endpoints'
import { Modal } from '../../components/ui/Modal'
import { usePluggyStatus } from '../pluggy/usePluggyStatus'
import { PluggyConfiguracaoStatus } from '../../lib/enums'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader, ProgressBar } from '../../components/ui/Card'
import { useToast } from '../../components/ui/Toast'
import { cn } from '../../lib/utils'
import { useTutorial } from './tutorialContext'
import { useAtualizarOnboarding, useOnboarding } from './useOnboarding'

const SUBTITULO: Record<string, string> = {
  conta: 'Crie uma conta manual ou, se quiser, conecte um banco',
  corte_saldo: 'Data de corte e saldo de hoje',
  cartao: 'Opcional · fechamento, vencimento e limite',
  categorias: 'Opcional · revise as que já vêm prontas',
  orcamento: 'Opcional · você controla o que gasta',
}

/** Cartão "Primeiros passos" do painel: some quando todos os passos estiverem concluídos ou ignorados. */
export function PrimeirosPassosCard() {
  const { data } = useOnboarding()
  const atualizar = useAtualizarOnboarding()
  const { iniciarTutorial } = useTutorial()
  const navigate = useNavigate()
  const toast = useToast()
  const [escolhendoConta, setEscolhendoConta] = useState(false)
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list() })
  const pluggy = usePluggyStatus()
  const { data: vinculos = [] } = useQuery({ queryKey: ['pluggy-vinculos'], queryFn: pluggyVinculosApi.list, retry: false })

  if (!data) return null
  // Contas antigas (não elegíveis e sem resposta ao convite) não veem o cartão.
  if (!data.elegivelConvite && !data.estado.conviteResposta) return null
  const { passos } = data
  if (passos.length === 0 || passos.every((p) => p.concluido || p.ignorado)) return null

  const feitos = passos.filter((p) => p.concluido).length
  const ignorados = data.estado.passosIgnorados

  function alternarIgnorado(id: string, ignorar: boolean) {
    const novos = ignorar ? [...new Set([...ignorados, id])] : ignorados.filter((x) => x !== id)
    atualizar.mutate({ passosIgnorados: novos })
  }

  function abrir(id: string) {
    if (id === 'conta') { setEscolhendoConta(true); return }
    if (id === 'corte_saldo') {
      // Conta conectada pendente: o assistente abre pela tela da Pluggy. Conta manual: modal de saldo inicial e corte.
      if (vinculos.some((v) => v.configuracaoStatus === PluggyConfiguracaoStatus.Pendente)) {
        navigate('/pluggy')
        toast.success('Toque em Configurar na conta conectada para escolher o corte e confirmar o saldo.')
        return
      }
      const manual = contas.find((c) => !c.dataAbertura && !vinculos.some((v) => v.financialAccountId === c.id))
      navigate(manual ? `/contas?saldoInicial=${manual.id}` : '/pluggy')
      return
    }
    iniciarTutorial('primeiros-passos', id)
  }

  return (
    <Card>
      <CardHeader
        title="Primeiros passos"
        icon="ri-rocket-line"
        actions={<span className="text-13 font-medium text-primary">{feitos} de {passos.length}</span>}
      />
      <CardBody>
        <ProgressBar value={(feitos / passos.length) * 100} />
        <ul className="mt-2">
          {passos.map((p, i) => (
            <li key={p.id} className="flex flex-wrap items-center gap-3 border-t border-border/60 py-2.5 first:border-t-0">
              <span
                className={cn(
                  'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-2xs font-semibold',
                  p.concluido ? 'bg-income text-bg' : p.ignorado ? 'bg-surface3 text-muted' : 'border border-border text-muted',
                )}
                aria-hidden
              >
                {p.concluido ? <i className="ri-check-line" /> : p.ignorado ? <i className="ri-subtract-line" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn('text-13 font-medium', p.concluido || p.ignorado ? 'text-muted' : 'text-white')}>{p.titulo}</p>
                <p className="text-2xs text-muted">
                  {p.concluido ? 'Concluído' : p.ignorado ? 'Ignorado' : (p.detalhe || SUBTITULO[p.id])}
                </p>
              </div>
              {!p.concluido && (
                <div className="flex gap-2">
                  {p.ignorado ? (
                    <Button size="sm" variant="ghost" onClick={() => alternarIgnorado(p.id, false)}>Desfazer</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="soft-primary" onClick={() => abrir(p.id)}>Abrir</Button>
                      {!p.obrigatorio && <Button size="sm" variant="ghost" onClick={() => alternarIgnorado(p.id, true)}>Ignorar</Button>}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-2xs text-muted">
          Cada passo se conclui sozinho quando você faz. O cartão some quando todos estiverem concluídos ou ignorados, e você pode rever tudo em Tutoriais.
        </p>
      </CardBody>
      <Modal open={escolhendoConta} onClose={() => setEscolhendoConta(false)} title="Como você quer começar?" size="md">
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            className="rounded border border-border p-4 text-left hover:border-primary"
            onClick={() => { setEscolhendoConta(false); navigate('/contas?nova=1') }}
          >
            <i className="ri-wallet-3-line text-xl text-primary" aria-hidden />
            <p className="mt-2 text-15 font-medium text-white">Criar conta manual</p>
            <p className="mt-1 text-13 text-muted">Você cadastra a conta e informa o saldo de hoje. Os lançamentos você registra ou importa da fatura do cartão (CSV).</p>
          </button>
          {pluggy.ativa ? (
            <button
              type="button"
              className="rounded border border-border p-4 text-left hover:border-primary"
              onClick={() => { setEscolhendoConta(false); navigate('/pluggy?conectar=1') }}
            >
              <i className="ri-bank-line text-xl text-primary" aria-hidden />
              <p className="mt-2 text-15 font-medium text-white">Conectar banco (opcional)</p>
              <p className="mt-1 text-13 text-muted">Traz saldo e lançamentos do banco, pela Pluggy. Mostramos um aviso antes de abrir a conexão.</p>
            </button>
          ) : (
            <div className="rounded border border-dashed border-border p-4 text-left opacity-80" role="note">
              <i className="ri-bank-line text-xl text-muted" aria-hidden />
              <p className="mt-2 text-15 font-medium text-muted">Conectar banco (opcional)</p>
              <p className="mt-1 text-13 text-warning">{pluggy.mensagem}</p>
            </div>
          )}
        </div>
        <p className="mt-3 text-13 text-muted">
          Os dois caminhos levam ao mesmo lugar: o app funciona completo sem conectar banco.{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => { setEscolhendoConta(false); iniciarTutorial('primeiros-passos', 'conta') }}>
            Ver o guia passo a passo
          </button>
        </p>
      </Modal>
    </Card>
  )
}
